import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Button } from '../../../../../components/ui/button';
import type { MaterialData } from '../../../types/materials';
import type { DashboardProject } from '../../../types/project';
import {
  NumberInput,
  parseNonNegativeInteger,
  parseNonNegativeNumber,
} from '../../../../../components/ui/NumberInput';
import { updateMaterialTraceability, AddMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { Notification } from '../../../../../components/ui/custom-notification';
import { convertValueToInches, convertValueFromInches } from '../../../../../utils/unitUtils';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { DocumentTag } from '../../../types';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { AntdDate } from '../../../../../components/ui/antd-date';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Plus, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import {
  buildInventorySegmentsFromMaterialDetails,
  createEmptyWarehouseSegment,
  resizeWarehouseSegments,
  type WarehousePipeSegment,
} from '../../../../analysis/utils/warehouseMaterialPayload';
import { logPipeInventoryMaterialUpdated } from '../../../../analysis/utils/pipeInventoryAudit';
import { isDualDiameterMaterialCategory, isPipeLikeMaterialCategory as isPipeLikeCategory } from './materialCategoryConstants';

interface UpdateMaterialDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (material: MaterialData) => void;
  material: MaterialData | null;
  project: DashboardProject;
  unit: string;
  documentTags: DocumentTag[];
  setDocumentTags: (any: any) => void;
  selectedPipe: any;
  /** Pipe Inventory edit: show pups → segment rows (same as Add Project Material). */
  pipeInventoryMode?: boolean;
}

// Validation rules for different material categories
const validationRules = {
  Pipe: ['pipeNo'],
  Forge: ['forgeNRI', 'forgeSerialNumber'],
  Gas: ['gasSerialNumber', 'gasLotNumber'],
  'Wire Lot': ['wireLotNumber'],
  NDE: ['consumableType'],
  Coating: ['coatingLotNumber'],
  Misc: ['miscQuantity'],
  Bends: ['pipeNo'],
  Flanges: ['pipeNo'],
  Reducer: ['pipeNo'],
  'T-Piece': ['pipeNo'],
  Weldolet: ['pipeNo'],
  'End Cap': ['pipeNo'],
  Sweepolet: ['pipeNo'],
  Hydro: ['pipeNo'],
  Torquing: ['pipeNo'],
  Gauging: ['pipeNo'],
  Flushing: ['pipeNo'],
};

// Unit options: display full names in dropdown, save short forms
const UNIT_OPTIONS: { value: string; label: string }[] = [
  { value: "in", label: "Inch" },
  { value: "cm", label: "Centimeter" },
  { value: "mm", label: "Millimeter" },
  { value: "ft", label: "Foot" },
  { value: "m", label: "Meter" },
];

// Field labels for error messages
const fieldLabels = {
  // status: 'Status',
  pipeNRI: 'Pipe NRI',
  pipeNo: 'Pipe No',
  pipeHeatNumber: 'Heat Number',
  pipeLength: 'Length',
  pipeOuterDiameter: 'Outer Diameter',
  pipeOuterDiameter2: 'Diameter 2',
  pipeWallThickness: 'Wall Thickness',
  pipeWallThickness2: 'Wall Thickness 2',
  forgeNRI: 'Forge NRI',
  forgeOrderNumber: 'Forge Order Number',
  forgeSerialNumber: 'Forge Serial Number',
  forgeHeatNumber: 'Forge Heat Number',
  gasSerialNumber: 'Gas Serial Number',
  gasType: 'Gas Type',
  gasLotNumber: 'Gas Lot Number',
  wireLotNumber: 'Wire Lot Number',
  wireType: 'Wire Type',
  coatingLotNumber: 'Coating Lot Number',
  coatingManufacturer: 'Coating Manufacturer',
  coatingBatchNumber: 'Coating Batch Number',

  wireManufacturer: 'Wire Manufacturer',
  gasManufacturer: 'Gas Manufacturer',
  forgeManufacturer: 'Forge Manufacturer',

  wireBatchNumber: 'Wire Batch Number',
  gasBatchNumber: 'Gas Batch Number',
  forgeBatchNumber: 'Forge Batch Number',
  consumableType: 'Consumable Type',
  ndeManufacturer: 'NDE Manufacturer',
  miscQuantity: 'Quantity',
};
export const UpdateMaterialDialog: React.FC<UpdateMaterialDialogProps> = ({
  isOpen,
  onClose,
  onUpdate,
  material,
  project,
  unit,
  documentTags,
  setDocumentTags,
  selectedPipe,
  pipeInventoryMode = false,
}) => {
  const [updatedMaterial, setUpdatedMaterial] = useState<Partial<MaterialData>>({
    materialCategory: 'Pipe',
    materialStatus: 'Available',
    details: {}
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // These arrays store the string representation of tag IDs (e.g., "101", "102")
  const [selectedPipeGrade, setSelectedPipeGrade] = useState<string[]>([]);
  const [selectedMaterialType, setSelectedMaterialType] = useState<string[]>([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string[]>([]);
  const [selectedNDEManufacturer, setSelectedNDEManufacturer] = useState<string[]>([]);
  const [isAddingPipeGrade, setIsAddingPipeGrade] = useState(false);
  const [isAddingMaterialType, setIsAddingMaterialType] = useState(false);
  const [isAddingWorkOrderNumber, setIsAddingWorkOrderNumber] = useState(false);
  const [isAddingPONumber, setIsAddingPONumber] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isAddingPipeType, setIsAddingPipeType] = useState(false);
  const [isAddingPipePslLevel, setIsAddingPipePslLevel] = useState(false);
  const [isAddingPipeDelivery, setIsAddingPipeDelivery] = useState(false);
  const [isAddingMiscLot, setIsAddingMiscLot] = useState(false);
  const [isAddingMiscMaterialType, setIsAddingMiscMaterialType] = useState(false);
  const [newTag, setNewTag] = useState('');
  // Material unit state for Pipe and Forge
  const [materialUnit, setMaterialUnit] = useState({
    pipeOuterDiameter: unit as string,
    pipeWallThickness: unit as string,
    pipeLength: unit as string,
    pipeLengthRemaining: unit as string,
    forgeOuterDiameter: unit as string,
    forgeWallThickness: unit as string,
    forgeLength: unit as string,
    forgeLengthRemaining: unit as string,
  });
  const handleMaterialUnitChange = (field: keyof typeof materialUnit, newUnit: string) => {
    const valueFieldMap: Record<keyof typeof materialUnit, string> = {
      pipeOuterDiameter: 'pipeOuterDiameter',
      pipeWallThickness: 'pipeWallThickness',
      pipeLength: 'pipeLength',
      pipeLengthRemaining: 'pipeLengthRemaining',
      forgeOuterDiameter: 'forgeOuterDiameter',
      forgeWallThickness: 'forgeWallThickness',
      forgeLength: 'forgeLength',
      forgeLengthRemaining: 'forgeLengthRemaining',
    };
    const valueField = valueFieldMap[field];
    const oldUnit = materialUnit[field];
    const currentValue = Number((updatedMaterial.details as any)?.[valueField]) || 0;
    const valueInInches = convertValueToInches(currentValue, oldUnit);
    const convertedValue = convertValueFromInches(valueInInches, newUnit);
    setMaterialUnit(prev => ({ ...prev, [field]: newUnit }));
    const updates: Record<string, number> = { [valueField]: convertedValue };
    if (field === 'pipeOuterDiameter' && updatedMaterial.details?.pipeOuterDiameter2 != null && updatedMaterial.details.pipeOuterDiameter2 !== '') {
      const diam2InInches = convertValueToInches(Number(updatedMaterial.details.pipeOuterDiameter2), oldUnit);
      updates.pipeOuterDiameter2 = convertValueFromInches(diam2InInches, newUnit);
    }
    if (field === 'pipeWallThickness' && updatedMaterial.details?.pipeWallThickness2 != null && updatedMaterial.details.pipeWallThickness2 !== '') {
      const wt2InInches = convertValueToInches(Number(updatedMaterial.details.pipeWallThickness2), oldUnit);
      updates.pipeWallThickness2 = convertValueFromInches(wt2InInches, newUnit);
    }
    setUpdatedMaterial(prev => ({
      ...prev,
      details: { ...prev.details, ...updates }
    }));
  };

  // Local state for documentTags to ensure immediate updates
  const [localDocumentTags, setLocalDocumentTags] = useState<DocumentTag[]>(documentTags);

  // Convert document tags to options for the MultiSelect component
  // value: string representation of tag ID, label: tag name
  const materialTypeOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_TYPE_TAGS')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  // Convert manufacturers to options for the MultiSelect component
  // Note: This uses manufacturers from selectedPipe, not from documentTags
  // value: string representation of manufacturer ID, label: manufacturer name
  const manufacturerOptions = useMemo(() =>
    selectedPipe && selectedPipe.manufacturers
      ? selectedPipe.manufacturers.map((manufacturer: any) => ({
        value: manufacturer.manufacturerId.toString(),
        label: manufacturer.manufacturerName
      }))
      : [], [selectedPipe]
  );

  // Convert document tags to options for the MultiSelect component
  // value: string representation of tag ID, label: tag name
  const pipeGradeOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_GRADE')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  const workOrderNumberOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_WORK_ORDER_NUMBER_TAGS' && tag.project_id === selectedPipe?.projectId)
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags, selectedPipe]
  );

  const poNumberOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_PO_NUMBER_TAGS' && tag.project_id === selectedPipe?.projectId)
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags, selectedPipe]
  );

  const locationOptions = useMemo(() =>
    localDocumentTags
      .filter((tag: any) => tag.tags_type === 'MATERIAL_LOCATION_TAGS')
      .map(tag => ({
        value: tag.tags_id.toString(),
        label: tag.tags_name
      })), [localDocumentTags]
  );

  const mergeBasePipeOptions = useCallback((defaults: string[], tags: { value: string; label: string }[]) => {
    const seen = new Set<string>();
    return [
      ...defaults.map((label) => ({ value: label, label })),
      ...tags,
    ].filter((option) => {
      const key = option.label.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);

  const pipeTypeOptions = useMemo(
    () => {
      const tagOptions = localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_TYPE_TAGS' && tag.project_id === selectedPipe?.projectId)
        .map((tag: any) => ({ value: tag.tags_name, label: tag.tags_name }));
      return mergeBasePipeOptions(['SAWL', 'SMLS', 'ERW'], tagOptions);
    },
    [localDocumentTags, selectedPipe?.projectId, mergeBasePipeOptions]
  );

  const pipePslLevelOptions = useMemo(
    () => {
      const tagOptions = localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_PSL_LEVEL_TAGS' && tag.project_id === selectedPipe?.projectId)
        .map((tag: any) => ({ value: tag.tags_name, label: tag.tags_name }));
      return mergeBasePipeOptions(['PSL1', 'PSL2'], tagOptions);
    },
    [localDocumentTags, selectedPipe?.projectId, mergeBasePipeOptions]
  );

  const pipeDeliveryOptions = useMemo(
    () => {
      const tagOptions = localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_DELIVERY_TAGS' && tag.project_id === selectedPipe?.projectId)
        .map((tag: any) => ({ value: tag.tags_name, label: tag.tags_name }));
      return mergeBasePipeOptions(['TMCP', 'Normalized', 'Quenched and Tempered'], tagOptions);
    },
    [localDocumentTags, selectedPipe?.projectId, mergeBasePipeOptions]
  );

  const miscLotNumberTagOptions = useMemo(
    () =>
      localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_MISC_LOT_NUMBER_TAGS' && tag.project_id === selectedPipe?.projectId)
        .map((tag: any) => ({ value: tag.tags_id.toString(), label: tag.tags_name })),
    [localDocumentTags, selectedPipe?.projectId]
  );

  const miscMaterialTypeOptions = useMemo(
    () =>
      localDocumentTags
        .filter((tag: any) => tag.tags_type === 'MATERIAL_MISC_MATERIAL_TYPE_TAGS' && tag.project_id === selectedPipe?.projectId)
        .map((tag: any) => ({ value: tag.tags_id.toString(), label: tag.tags_name })),
    [localDocumentTags, selectedPipe?.projectId]
  );

  const miscMaterialTypeSelectOptions = useMemo(() => {
    const merged = [...miscMaterialTypeOptions];
    const selName = updatedMaterial.details?.miscMaterialTypeName?.trim();
    if (selName && !merged.some((o) => o.label === selName)) {
      const fallback = materialTypeOptions.find((o) => o.label === selName);
      if (fallback) merged.push(fallback);
    }
    return merged;
  }, [miscMaterialTypeOptions, materialTypeOptions, updatedMaterial.details?.miscMaterialTypeName]);

  const inventorySegments: WarehousePipeSegment[] = Array.isArray(
    (updatedMaterial.details as { pipeSegmentation?: WarehousePipeSegment[] } | undefined)
      ?.pipeSegmentation
  )
    ? (updatedMaterial.details as { pipeSegmentation: WarehousePipeSegment[] }).pipeSegmentation
    : [];

  const setInventorySegments = (segments: WarehousePipeSegment[], numberOfPups?: string | number | '') => {
    setUpdatedMaterial((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        pipeSegmentation: segments,
        pipeExtraDetails: {
          ...(prev.details?.pipeExtraDetails || {}),
          numberOfPups:
            numberOfPups === undefined
              ? prev.details?.pipeExtraDetails?.numberOfPups
              : numberOfPups === '' || numberOfPups == null
                ? ''
                : parseNonNegativeInteger(String(numberOfPups)),
        },
      },
    }));
  };

  const handleInventoryPupsChange = (value: number | string | '') => {
    const raw = value === '' || value == null ? '' : value;
    const count = raw === '' ? 0 : parseNonNegativeInteger(String(raw));
    const dateIso =
      (updatedMaterial.details as { dateRecorded?: string } | undefined)?.dateRecorded?.slice(0, 10) ||
      dayjs().format('YYYY-MM-DD');
    setInventorySegments(
      resizeWarehouseSegments(inventorySegments, Number.isFinite(count) ? count : 0, dateIso),
      raw
    );
  };

  // Effect to populate form when material is provided
  useEffect(() => {
    if (isOpen && material) {
      // Read units from root (material.units), fallback to details for backwards compatibility
      const rootUnits = material.units || {};
      const pipeUnit = {
        pipeOuterDiameter: rootUnits.pipeOuterDiameter ?? unit,
        pipeWallThickness: rootUnits.pipeWallThickness ?? unit,
        pipeLength: rootUnits.pipeLength ?? unit,
        pipeLengthRemaining: rootUnits.pipeLengthRemaining ?? unit
      };
      const forgeUnit = {
        forgeOuterDiameter: rootUnits.forgeOuterDiameter ?? unit,
        forgeWallThickness: rootUnits.forgeWallThickness ?? unit,
        forgeLength: rootUnits.forgeLength ?? unit,
        forgeLengthRemaining: rootUnits.forgeLengthRemaining ?? unit
      };
      setMaterialUnit({
        pipeOuterDiameter: rootUnits.pipeOuterDiameter ?? unit,
        pipeWallThickness: rootUnits.pipeWallThickness ?? unit,
        pipeLength: rootUnits.pipeLength ?? unit,
        pipeLengthRemaining: rootUnits.pipeLengthRemaining ?? unit,
        forgeOuterDiameter: rootUnits.forgeOuterDiameter ?? unit,
        forgeWallThickness: rootUnits.forgeWallThickness ?? unit,
        forgeLength: rootUnits.forgeLength ?? unit,
        forgeLengthRemaining: rootUnits.forgeLengthRemaining ?? unit,
      });
      // Initialize with basic material data
      let initialDetails = { ...material.details };
      // Convert from inches (API) to user's display units
      if (material.details?.pipeLength) {
        initialDetails.pipeLength = convertValueFromInches(Number(material.details.pipeLength), pipeUnit.pipeLength ?? unit);
      }
      if (material.details?.pipeOuterDiameter) {
        initialDetails.pipeOuterDiameter = convertValueFromInches(Number(material.details.pipeOuterDiameter), pipeUnit.pipeOuterDiameter ?? unit);
      }
      if (material.details?.pipeOuterDiameter2) {
        initialDetails.pipeOuterDiameter2 = convertValueFromInches(Number(material.details.pipeOuterDiameter2), pipeUnit.pipeOuterDiameter ?? unit);
      }
      if (material.details?.pipeWallThickness) {
        initialDetails.pipeWallThickness = convertValueFromInches(Number(material.details.pipeWallThickness), pipeUnit.pipeWallThickness ?? unit);
      }
      if (material.details?.pipeWallThickness2) {
        initialDetails.pipeWallThickness2 = convertValueFromInches(Number(material.details.pipeWallThickness2), pipeUnit.pipeWallThickness ?? unit);
      }
      if (material.details?.forgeLength) {
        initialDetails.forgeLength = convertValueFromInches(Number(material.details.forgeLength), forgeUnit.forgeLength ?? unit);
      }
      if (material.details?.forgeOuterDiameter) {
        initialDetails.forgeOuterDiameter = convertValueFromInches(Number(material.details.forgeOuterDiameter), forgeUnit.forgeOuterDiameter ?? unit);
      }
      if (material.details?.forgeWallThickness) {
        initialDetails.forgeWallThickness = convertValueFromInches(Number(material.details.forgeWallThickness), forgeUnit.forgeWallThickness ?? unit);
      }
      // Handle pipeExtraDetails - restore values from root level if they exist
      if (isPipeLikeCategory(material.materialCategory)) {
        initialDetails = {
          ...initialDetails,
          pipeWorkOrderNumber: material.details?.pipeWorkOrderNumber || material.details?.pipeExtraDetails?.pipeWorkOrderNumber,
          pipePurchaseOrderNumber: material.details?.pipePurchaseOrderNumber || material.details?.pipeExtraDetails?.pipePurchaseOrderNumber,
          pipeLocation: material.details?.pipeLocation || material.details?.pipeExtraDetails?.pipeLocation,
          pipeLotNumber: material.details?.pipeLotNumber || material.details?.pipeExtraDetails?.pipeLotNumber,
          pipeType: material.details?.pipeType || material.details?.pipeExtraDetails?.pipeType,
          pipePslLevel: material.details?.pipePslLevel || material.details?.pipeExtraDetails?.pipePslLevel,
          pipeDelivery: material.details?.pipeDelivery || material.details?.pipeExtraDetails?.pipeDelivery,
          pipeLengthRemaining: material.details?.pipeLengthRemaining 
            ? convertValueFromInches(Number(material.details.pipeLengthRemaining), pipeUnit.pipeLengthRemaining ?? unit)
            : material.details?.pipeExtraDetails?.pipeLengthRemaining 
            ? convertValueFromInches(Number(material.details.pipeExtraDetails.pipeLengthRemaining), pipeUnit.pipeLengthRemaining ?? unit)
            : undefined,
          pipeWeightPerFt: material.details?.pipeWeightPerFt || material.details?.pipeExtraDetails?.pipeWeightPerFt,
          pipeExtraDetails: {
            ...(material.details?.pipeExtraDetails || {}),
            wpsNumber: material.details?.pipeExtraDetails?.wpsNumber,
            contractor: material.details?.pipeExtraDetails?.contractor,
            numberOfPups: material.details?.pipeExtraDetails?.numberOfPups,
          },
        };
        if (pipeInventoryMode) {
          const segments = buildInventorySegmentsFromMaterialDetails(
            initialDetails as Record<string, unknown>
          );
          initialDetails = {
            ...initialDetails,
            pipeSegmentation: segments,
            mtrReceived: Boolean(
              (material.details as { mtrReceived?: boolean } | undefined)?.mtrReceived
            ),
            dateRecorded:
              (material.details as { dateRecorded?: string } | undefined)?.dateRecorded?.slice(
                0,
                10
              ) || dayjs().format('YYYY-MM-DD'),
          };
        }
      }
      // Handle forgeExtraDetails - restore values from root level if they exist
      if (material.materialCategory === 'Forge') {
        initialDetails.forgeWorkOrderNumber = material.details?.forgeWorkOrderNumber || material.details?.forgeExtraDetails?.forgeWorkOrderNumber;
        initialDetails.forgePurchaseOrderNumber = material.details?.forgePurchaseOrderNumber || material.details?.forgeExtraDetails?.forgePurchaseOrderNumber;
        initialDetails.forgeLocation = material.details?.forgeLocation || material.details?.forgeExtraDetails?.forgeLocation;
        initialDetails.forgeProjectStage = material.details?.forgeProjectStage || material.details?.forgeExtraDetails?.forgeProjectStage;
        initialDetails.forgeHeatNumber = material.details?.forgeHeatNumber || material.details?.forgeExtraDetails?.forgeHeatNumber;
        initialDetails.forgeNotes = material.details?.forgeNotes || material.details?.forgeExtraDetails?.forgeNotes;
        initialDetails.forgeLengthRemaining = material.details?.forgeLengthRemaining 
            ? convertValueFromInches(Number(material.details.forgeLengthRemaining), forgeUnit.forgeLengthRemaining ?? unit)
            : material.details?.forgeExtraDetails?.forgeLengthRemaining 
            ? convertValueFromInches(Number(material.details.forgeExtraDetails.forgeLengthRemaining), forgeUnit.forgeLengthRemaining ?? unit)
            : undefined;
        initialDetails.forgeWeightPerFt = material.details?.forgeWeightPerFt || material.details?.forgeExtraDetails?.forgeWeightPerFt;
      }
      
      // Handle ndeExtraDetails - restore values from root level if they exist
      if (material.materialCategory === 'NDE') {
        initialDetails.ndeExtraDetails = {
          ...initialDetails.ndeExtraDetails,
          ndeMaterialId: material.details?.ndeExtraDetails?.ndeMaterialId || material.details?.materialId || material.details?.material_id,
          ndeExpDate: material.details?.ndeExtraDetails?.ndeExpDate || material.details?.expDate || material.details?.expiry_date,
          ndeManufacturer: material.details?.ndeExtraDetails?.ndeManufacturer
        };
      }

      if (material.materialCategory === 'Misc') {
        const d: any = material.details || {};
        const ex: any = d.miscExtraDetails || {};

        let typeName = String(d.miscMaterialTypeName ?? '').trim();
        if (!typeName && typeof d.miscMaterialTypeLabels === 'string') {
          typeName = d.miscMaterialTypeLabels.split(',')[0].trim();
        }
        if (!typeName && d.miscMaterialType?.length) {
          const id = String(d.miscMaterialType[0]);
          const miscTag = documentTags.find(
            (t: any) => t.tags_type === 'MATERIAL_MISC_MATERIAL_TYPE_TAGS' && String(t.tags_id) === id
          );
          const genTag = documentTags.find(
            (t: any) => t.tags_type === 'MATERIAL_TYPE_TAGS' && String(t.tags_id) === id
          );
          typeName = miscTag?.tags_name ?? genTag?.tags_name ?? '';
        }
        initialDetails.miscMaterialTypeName = typeName;

        if (Array.isArray(d.miscMaterialTypeIds) && d.miscMaterialTypeIds.length) {
          initialDetails.miscMaterialTypeIds = d.miscMaterialTypeIds;
        } else if (Array.isArray(d.misc_material_type_ids) && d.misc_material_type_ids.length) {
          initialDetails.miscMaterialTypeIds = d.misc_material_type_ids;
        }

        const isEmpty = (v: unknown) => v === undefined || v === null || v === '';
        const pick = (camel: string, snake: string) => {
          if (!isEmpty(d[camel])) return d[camel];
          if (!isEmpty(d[snake])) return d[snake];
          if (!isEmpty(ex[camel])) return ex[camel];
          if (!isEmpty(ex[snake])) return ex[snake];
          return undefined;
        };

        const miscFieldPairs: [string, string][] = [
          ['miscPartNumber', 'misc_part_number'],
          ['miscHeatNumber', 'misc_heat_number'],
          ['miscLotNumber', 'misc_lot_number'],
          ['miscJobNumber', 'misc_job_number'],
          ['miscPoNumber', 'misc_po_number'],
          ['miscSizeOrWeight', 'misc_size_or_weight'],
          ['miscLocation', 'misc_location'],
          ['miscNotes', 'misc_notes'],
        ];
        for (const [camel, snake] of miscFieldPairs) {
          if (isEmpty((initialDetails as any)[camel])) {
            const v = pick(camel, snake);
            if (!isEmpty(v)) (initialDetails as any)[camel] = v;
          }
        }

        if (isEmpty((initialDetails as any).miscQuantity)) {
          const q = pick('miscQuantity', 'misc_quantity');
          if (!isEmpty(q)) (initialDetails as any).miscQuantity = typeof q === 'number' ? q : Number(q);
        }

        const prodType =
          (!isEmpty(d.miscProductionType) ? d.miscProductionType : undefined) ||
          (!isEmpty(d.miscProjectStage) ? d.miscProjectStage : undefined) ||
          (!isEmpty(ex.miscProductionType) ? ex.miscProductionType : undefined) ||
          (!isEmpty(ex.miscProjectStage) ? ex.miscProjectStage : undefined) ||
          pick('miscProductionType', 'misc_production_type') ||
          pick('miscProjectStage', 'misc_project_stage');
        if (!isEmpty(prodType)) {
          initialDetails.miscProductionType = prodType;
          initialDetails.miscProjectStage = prodType;
        }
      }

      const extraFromApi =
        material.materialExtraDetails ||
        (material.details as any)?.material_extra_details ||
        {};
      setUpdatedMaterial({
        materialId: material.materialId,
        materialCategory: material.materialCategory,
        materialStatus: material.materialStatus,
        materialExtraDetails: { ...extraFromApi },
        details: initialDetails
      });

      // Populate selected arrays if the material has existing tag IDs
      if (material.details?.pipeGrade) {
        setSelectedPipeGrade(material.details.pipeGrade.map((id: number) => id.toString()));
      }
      if (material.details?.forgeGrade) {
        setSelectedPipeGrade(material.details.forgeGrade.map((id: number) => id.toString()));
      }
      if (material.materialCategory === 'Misc') {
        setSelectedMaterialType([]);
      } else if (material.details?.pipeMaterialType) {
        setSelectedMaterialType(material.details.pipeMaterialType.map((id: number) => id.toString()));
      }

      if (material.materialManufacturerId && material.materialManufacturerId.length > 0) {
        setSelectedManufacturer(material.materialManufacturerId.map((id: number) => id.toString()));
      } else if (
        material.materialCategory === 'Misc' &&
        material.details?.manufacturerIds &&
        material.details.manufacturerIds.length > 0
      ) {
        setSelectedManufacturer(material.details.manufacturerIds.map((id: number) => id.toString()));
      } else if (
        material.materialCategory === 'Misc' &&
        material.details?.miscExtraDetails?.miscManufacturer &&
        manufacturerOptions.length > 0
      ) {
        const manufacturerNames = String(material.details.miscExtraDetails.miscManufacturer)
          .split(',')
          .map((name: string) => name.trim())
          .filter(Boolean);
        const manufacturerIds = manufacturerNames
          .map((name: string) => manufacturerOptions.find((opt: any) => opt.label === name)?.value)
          .filter((id: string | undefined): id is string => !!id);
        setSelectedManufacturer(manufacturerIds);
      } else if (material.details?.pipeExtraDetails?.pipeManufacturer) {
        const manufacturerId = manufacturerOptions.find(
          (option: any) => option.label === material.details.pipeExtraDetails.pipeManufacturer
        );
        if (manufacturerId) {
          setSelectedManufacturer([manufacturerId.value]);
        } else {
          setSelectedManufacturer([]);
        }
      } else {
        setSelectedManufacturer([]);
      }

      // Handle NDE manufacturer restoration
      if (material.materialCategory === 'NDE') {
        if (material.materialManufacturerId && material.materialManufacturerId.length > 0) {
          // Restore from manufacturer IDs
          setSelectedNDEManufacturer(material.materialManufacturerId.map((id: number) => id.toString()));
        } else if (material.details?.manufacturerIds && material.details.manufacturerIds.length > 0) {
          // Restore from details.manufacturerIds
          setSelectedNDEManufacturer(material.details.manufacturerIds.map((id: number) => id.toString()));
        } else if (material.details?.ndeExtraDetails?.ndeManufacturer && manufacturerOptions.length > 0) {
          // Restore from manufacturer name/label (comma-separated string)
          const manufacturerNames = material.details.ndeExtraDetails.ndeManufacturer.split(',').map((name: string) => name.trim());
          const manufacturerIds = manufacturerNames
            .map((name: string) => {
              const option = manufacturerOptions.find((opt: any) => opt.label === name);
              return option ? option.value : null;
            })
            .filter((id: string | null) => id !== null) as string[];
          if (manufacturerIds.length > 0) {
            setSelectedNDEManufacturer(manufacturerIds);
          }
        }
      }
    }
  }, [isOpen, material, manufacturerOptions, documentTags, pipeInventoryMode]);

  // Sync local state with parent state when documentTags change
  useEffect(() => {
    setLocalDocumentTags(documentTags);
  }, [documentTags]);

  // Initialize local state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalDocumentTags(documentTags);
    }
  }, [isOpen, documentTags]);



  // Validation helper function
  const validateMaterialFields = (category: string, material: any): string | null => {


    const requiredFields = validationRules[category as keyof typeof validationRules];

    for (const field of requiredFields) {
      const value = material[field] || material.details?.[field];
      const numVal = typeof value === 'number' ? value : Number(value);

      if (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value === 'number' && value <= 0) ||
        (field === 'miscQuantity' && (!Number.isFinite(numVal) || numVal <= 0))
      ) {
        const errorMessage = `${fieldLabels[field as keyof typeof fieldLabels]} is required`;
        return errorMessage;
      }
    }

    return null;
  };

  // Handle update material
  const handleUpdateMaterial = async () => {
    if (isSubmitting) {
      return;
    }


    try {
      // Basic validation
      if (!updatedMaterial.materialCategory) {
        Notification.warning('Please select a material category');
        return;
      }

      // Validate required fields based on category
      const validationError = validateMaterialFields(updatedMaterial.materialCategory, updatedMaterial);
      if (validationError) {
        Notification.warning('Please fill the required fields', validationError);
        return;
      }

      if (updatedMaterial.materialCategory === 'Misc' && !updatedMaterial.details?.miscMaterialTypeName?.trim()) {
        Notification.warning('Please select a misc material type');
        return;
      }

      setIsSubmitting(true);

      // Prepare material data for update
      /** Prefer materialExtraDetails.materialStatus; else root materialStatus (Consumed cannot revert to Available). */
      const effectiveMaterialStatus =
        (updatedMaterial.materialExtraDetails?.materialStatus as
          | 'Available'
          | 'Consumed'
          | 'Shipped to NDT'
          | undefined) ??
        updatedMaterial.materialStatus ??
        'Available';
      const apiRootStatus =
        effectiveMaterialStatus === 'Shipped to NDT' ? 'Consumed' : effectiveMaterialStatus;

      const materialData = [{
        materialId: updatedMaterial.materialId,
        materialStatus: apiRootStatus,
        materialCategory: updatedMaterial.materialCategory,
        materialManufacturerId:
          updatedMaterial.materialCategory === 'NDE'
            ? selectedNDEManufacturer.map((id) => Number(id))
            : selectedManufacturer.map((id) => Number(id)),

          units: materialUnit,
        // Convert string IDs back to numbers for API

        details: {
          ...updatedMaterial.details,
          // Convert length fields from feet to inches for storage
        
        },
        materialExtraDetails: {
          ...(updatedMaterial.materialExtraDetails || {}),
          materialStatus: effectiveMaterialStatus,
        },
      }];
      if (isPipeLikeCategory(updatedMaterial.materialCategory)) {
        const rawPipeNri = materialData[0].details?.pipeNRI;
        materialData[0].details.pipeNRI =
          rawPipeNri == null || String(rawPipeNri).trim() === '' ? null : String(rawPipeNri).trim();
        // Convert string IDs back to numbers for API
        materialData[0].details.pipeGrade = selectedPipeGrade.map(id => Number(id));
        materialData[0].details.pipeMaterialType = selectedMaterialType.map(id => Number(id));
        materialData[0].details.manufacturerIds = selectedManufacturer.map(id => Number(id));
        const pipeManufacturerLabel = selectedManufacturer
          .map((id: string) => manufacturerOptions.find((o: any) => o.value === id)?.label)
          .filter(Boolean)
          .join(', ');
        if (pipeManufacturerLabel) {
          materialData[0].details.manufacturerLabel = pipeManufacturerLabel;
        }

        // Convert from user's display units to inches for storage (DB always stores inches)
        materialData[0].details.pipeLength = updatedMaterial.details?.pipeLength ? convertValueToInches(Number(updatedMaterial.details.pipeLength), materialUnit.pipeLength) : undefined;
        materialData[0].details.pipeOuterDiameter = updatedMaterial.details?.pipeOuterDiameter ? convertValueToInches(Number(updatedMaterial.details.pipeOuterDiameter), materialUnit.pipeOuterDiameter) : undefined;
        materialData[0].details.pipeOuterDiameter2 = updatedMaterial.details?.pipeOuterDiameter2 ? convertValueToInches(Number(updatedMaterial.details.pipeOuterDiameter2), materialUnit.pipeOuterDiameter) : undefined;
        materialData[0].details.pipeWallThickness = updatedMaterial.details?.pipeWallThickness ? convertValueToInches(Number(updatedMaterial.details.pipeWallThickness), materialUnit.pipeWallThickness) : undefined;
        materialData[0].details.pipeWallThickness2 = updatedMaterial.details?.pipeWallThickness2 ? convertValueToInches(Number(updatedMaterial.details.pipeWallThickness2), materialUnit.pipeWallThickness) : undefined;
        materialData[0].details.pipeLengthRemaining = updatedMaterial.details?.pipeLengthRemaining ? convertValueToInches(Number(updatedMaterial.details.pipeLengthRemaining), materialUnit.pipeLengthRemaining) : undefined;
        (materialData[0] as any).units = {
          pipeOuterDiameter: materialUnit.pipeOuterDiameter,
          pipeOuterDiameter2: materialUnit.pipeOuterDiameter,
          pipeWallThickness: materialUnit.pipeWallThickness,
          pipeWallThickness2: materialUnit.pipeWallThickness,
          pipeLength: materialUnit.pipeLength,
          pipeLengthRemaining: materialUnit.pipeLengthRemaining
        };
        materialData[0].details.pipeExtraDetails = {
          ...updatedMaterial.details?.pipeExtraDetails,
          pipeLotNumber: updatedMaterial.details?.pipeLotNumber || undefined,
          pipeType: updatedMaterial.details?.pipeType || undefined,
          pipePslLevel: updatedMaterial.details?.pipePslLevel || undefined,
          pipeDelivery: updatedMaterial.details?.pipeDelivery || undefined,
          pipeLengthRemaining: updatedMaterial.details?.pipeLengthRemaining ? convertValueToInches(Number(updatedMaterial.details.pipeLengthRemaining), materialUnit.pipeLengthRemaining) : undefined,
          pipeWeightPerFt: updatedMaterial.details?.pipeWeightPerFt ? Number(updatedMaterial.details.pipeWeightPerFt) : undefined,
          wpsNumber: updatedMaterial.details?.pipeExtraDetails?.wpsNumber?.trim() || undefined,
          contractor: updatedMaterial.details?.pipeExtraDetails?.contractor?.trim() || undefined,
          manufacturerLabel: pipeManufacturerLabel || undefined,
          numberOfPups:
            updatedMaterial.details?.pipeExtraDetails?.numberOfPups != null &&
            updatedMaterial.details.pipeExtraDetails.numberOfPups !== ''
              ? parseNonNegativeInteger(
                  String(updatedMaterial.details.pipeExtraDetails.numberOfPups)
                )
              : undefined,
        };
        if (pipeInventoryMode) {
          const detailsAny = materialData[0].details as Record<string, unknown>;
          const dateRecorded = (updatedMaterial.details as { dateRecorded?: string } | undefined)
            ?.dateRecorded;
          detailsAny.mtrReceived = Boolean(
            (updatedMaterial.details as { mtrReceived?: boolean } | undefined)?.mtrReceived
          );
          detailsAny.dateRecorded = dateRecorded
            ? new Date(dateRecorded).toISOString()
            : null;
          detailsAny.pipeSegmentation =
            inventorySegments.length > 0
              ? inventorySegments.map((seg) => ({
                  id: seg.id,
                  segmentName: seg.segmentName,
                  segmentLength: parseNonNegativeNumber(String(seg.segmentLength)),
                  dateInventory: seg.dateInventory
                    ? new Date(seg.dateInventory).toISOString()
                    : new Date().toISOString(),
                  used: seg.used,
                }))
              : undefined;
          if (materialData[0].details.pipeLength != null) {
            materialData[0].details.pipeLength = parseNonNegativeNumber(
              String(materialData[0].details.pipeLength)
            );
          }
        }
      } else if (updatedMaterial.materialCategory === 'Forge') {
        // Convert string IDs back to numbers for API
        materialData[0].details.forgeGrade = selectedPipeGrade.map(id => Number(id)); // Using pipeGrade for forge grade
        materialData[0].details.pipeMaterialType = selectedMaterialType.map(id => Number(id));
        materialData[0].details.manufacturerIds = selectedManufacturer.map(id => Number(id));
        
        if(updatedMaterial.details?.forgeLength){
          materialData[0].details.forgeLength = updatedMaterial.details?.forgeLength ? convertValueToInches(Number(updatedMaterial.details.forgeLength), materialUnit.forgeLength) : undefined;
        }
        if(updatedMaterial.details?.forgeOuterDiameter){
          materialData[0].details.forgeOuterDiameter = updatedMaterial.details?.forgeOuterDiameter ? convertValueToInches(Number(updatedMaterial.details.forgeOuterDiameter), materialUnit.forgeOuterDiameter) : undefined;
        }
        if(updatedMaterial.details?.forgeWallThickness){
          materialData[0].details.forgeWallThickness = updatedMaterial.details?.forgeWallThickness ? convertValueToInches(Number(updatedMaterial.details.forgeWallThickness), materialUnit.forgeWallThickness) : undefined;
        }
        if(updatedMaterial.details?.forgeLengthRemaining){
          materialData[0].details.forgeLengthRemaining = updatedMaterial.details?.forgeLengthRemaining ? convertValueToInches(Number(updatedMaterial.details.forgeLengthRemaining), materialUnit.forgeLengthRemaining) : undefined;
        }
        (materialData[0] as any).units = {
          forgeOuterDiameter: materialUnit.forgeOuterDiameter,
          forgeWallThickness: materialUnit.forgeWallThickness,
          forgeLength: materialUnit.forgeLength,
          forgeLengthRemaining: materialUnit.forgeLengthRemaining
        };
        if(updatedMaterial.details?.forgeWeightPerFt){
          materialData[0].details.forgeWeightPerFt = updatedMaterial.details?.forgeWeightPerFt ? Number(updatedMaterial.details.forgeWeightPerFt) : undefined;
        }
      
        
        // Add forge-specific extra details with proper unit conversions
        materialData[0].details.forgeExtraDetails = {
          ...updatedMaterial.details?.forgeExtraDetails,
          forgeLengthRemaining: updatedMaterial.details?.forgeExtraDetails?.forgeLengthRemaining ? convertValueToInches(Number(updatedMaterial.details.forgeExtraDetails.forgeLengthRemaining), materialUnit.forgeLengthRemaining) : undefined,
          forgeWeightPerFt: updatedMaterial.details?.forgeExtraDetails?.forgeWeightPerFt ? Number(updatedMaterial.details.forgeExtraDetails.forgeWeightPerFt) : undefined
        };
      } else if (updatedMaterial.materialCategory === 'NDE') {
        // Add manufacturer IDs for NDE
        materialData[0].details.manufacturerIds = selectedNDEManufacturer.map(id => Number(id));
        materialData[0].materialManufacturerId = selectedNDEManufacturer.map(id => Number(id));
        // Get manufacturer labels from manufacturerOptions
        const manufacturerLabels = selectedNDEManufacturer.map((id: string) => {
          const option = manufacturerOptions.find((opt: any) => opt.value === id);
          return option ? option.label : '';
        }).filter((label: string) => label !== '');
        // Save manufacturer labels, materialId, and expiry date to ndeExtraDetails
        materialData[0].details.ndeExtraDetails = {
          ...materialData[0].details.ndeExtraDetails,
          ndeManufacturer: manufacturerLabels.join(', '),
          ndeMaterialId: updatedMaterial.details?.ndeExtraDetails?.ndeMaterialId || updatedMaterial.details?.materialId || updatedMaterial.details?.material_id,
          ndeExpDate: updatedMaterial.details?.ndeExtraDetails?.ndeExpDate || updatedMaterial.details?.expDate || updatedMaterial.details?.expiry_date
        };
      } else if (updatedMaterial.materialCategory === 'Misc') {
        const typeName = updatedMaterial.details?.miscMaterialTypeName?.trim() || '';
        const opt =
          miscMaterialTypeOptions.find((o: any) => o.label === typeName) ??
          materialTypeOptions.find((o: any) => o.label === typeName);
        const typeIds = opt ? [Number(opt.value)] : [];
        materialData[0].details.miscMaterialType = typeIds;
        materialData[0].details.miscMaterialTypeLabels = typeName;
        materialData[0].details.miscMaterialTypeName = typeName;
        if (typeIds.length) {
          materialData[0].details.miscMaterialTypeIds = typeIds;
        }
        materialData[0].details.manufacturerIds = selectedManufacturer.map((id) => Number(id));
        materialData[0].materialManufacturerId = selectedManufacturer.map((id) => Number(id));
        const manuLabels = selectedManufacturer
          .map((id: string) => manufacturerOptions.find((o: any) => o.value === id)?.label)
          .filter(Boolean)
          .join(', ');
        materialData[0].details.miscExtraDetails = {
          ...materialData[0].details.miscExtraDetails,
          miscManufacturer: manuLabels,
        };
        materialData[0].details.miscQuantity =
          updatedMaterial.details?.miscQuantity !== undefined && updatedMaterial.details?.miscQuantity !== ''
            ? Number(updatedMaterial.details.miscQuantity)
            : undefined;
      }

      const apiPayload = {
        data: materialData
      };

      const result = await updateMaterialTraceability(apiPayload);

      if (result) {
        if (pipeInventoryMode) {
          logPipeInventoryMaterialUpdated(result as MaterialData);
        }
        await onUpdate(result);
        onClose();
        resetForm();
      } else {
        Notification.warning('Failed to update material. Please try again.');
      }

    } catch (error) {
      Notification.warning('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPipeGrade = async (gradeName: string) => {
    if (gradeName.trim()) {
      if (pipeGradeOptions.find((tag: any) => tag.label === gradeName.trim())) {
        Notification.warning(`${gradeName.trim()} grade already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: gradeName.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_PIPE_GRADE',
        created_by: 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Material pipe grade Tag');
      if (res) {

        // Add the new tag to documentTags
        const newTag = {
          tags_name: res.tags_name,
          tags_id: res.tags_id,
          tags_type: res.tags_type
        };


        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, newTag];
        setLocalDocumentTags(updatedLocalTags);

        // Also update parent state
        setDocumentTags(updatedLocalTags);


        // Automatically select the newly added tag
        setSelectedPipeGrade(prev => [...prev, res.tags_id.toString()]);

        // Also update the material details to include the new tag ID
        setUpdatedMaterial(prev => ({
          ...prev,
          details: {
            ...prev.details,
            pipeGradeIds: [...(prev.details?.pipeGradeIds || []), res.tags_id]
          }
        }));

      }
    }
  }

  const handleAddMaterialType = async (typeName: string) => {
    if (typeName.trim()) {
      if (materialTypeOptions.find((tag: any) => tag.label === typeName.trim())) {
        Notification.warning(`${typeName.trim()} material type already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: typeName.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_TYPE_TAGS',
        created_by: 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Material Type Tag');
      if (res) {

        // Add the new tag to documentTags
        const newTag = {
          tags_name: res.tags_name,
          tags_id: res.tags_id,
          tags_type: res.tags_type
        };



        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, newTag];
        setLocalDocumentTags(updatedLocalTags);

        // Also update parent state
        setDocumentTags(updatedLocalTags);


        setSelectedMaterialType((prev) => [...prev, res.tags_id.toString()]);

        setUpdatedMaterial((prev) => ({
          ...prev,
          details: {
            ...prev.details,
            materialTypeIds: [...(prev.details?.materialTypeIds || []), res.tags_id],
          },
        }));

      }
    }
  }

  const handleAddMiscMaterialType = async (typeName: string) => {
    if (!typeName.trim() || !selectedPipe?.projectId) return;
    if (miscMaterialTypeOptions.find((tag: any) => tag.label === typeName.trim())) {
      Notification.warning(`${typeName.trim()} already exists`);
      setIsAddingMiscMaterialType(false);
      return;
    }
    const apiPayload = {
      tags_name: typeName.trim(),
      project_id: selectedPipe.projectId,
      tags_type: 'MATERIAL_MISC_MATERIAL_TYPE_TAGS',
      created_by: 'admin',
    };
    const res = await AddMaterialCategory(apiPayload, 'Misc material type');
    if (res) {
      const newTag = {
        tags_name: res.tags_name,
        tags_id: res.tags_id,
        tags_type: res.tags_type,
      };
      const updatedLocalTags = [...localDocumentTags, newTag];
      setLocalDocumentTags(updatedLocalTags);
      setDocumentTags(updatedLocalTags);
      setUpdatedMaterial((prev) => ({
        ...prev,
        details: {
          ...prev.details,
          miscMaterialTypeName: typeName.trim(),
          miscMaterialTypeIds: [res.tags_id],
        },
      }));
    }
    setIsAddingMiscMaterialType(false);
  };

  const handleAddWorkOrderNumber = async (workOrderNumber: string) => {
    if (workOrderNumber.trim()) {
      if (workOrderNumberOptions.find((tag: any) => tag.label === workOrderNumber.trim())) {
        Notification.warning(`${workOrderNumber.trim()} work order number already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: workOrderNumber.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_WORK_ORDER_NUMBER_TAGS',
        created_by: 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Work Order Number Tag');
      if (res) {
        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);

        if (isPipeLikeCategory(updatedMaterial.materialCategory)) {
          // Update the material details with the new work order number
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              pipeWorkOrderNumber: workOrderNumber.trim(),
              // Also update forge fields if this is a forge material
              pipeOrderNumber: workOrderNumber.trim()
            }
          }))
        }
        else if (updatedMaterial.materialCategory === 'Misc') {
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              miscJobNumber: workOrderNumber.trim(),
            }
          }))
        }
        else {
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              forgeWorkOrderNumber: workOrderNumber.trim(),
              forgeOrderNumber: workOrderNumber.trim()
            }
          }))
        }

      }

    }

    setIsAddingWorkOrderNumber(false);
  }

  const handleAddPONumber = async (poNumber: string) => {
    if (poNumber.trim()) {
      if (poNumberOptions.find((tag: any) => tag.label === poNumber.trim())) {
        Notification.warning(`${poNumber.trim()} PO number already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: poNumber.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_PO_NUMBER_TAGS',
        created_by: 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'PO Number Tag');
      if (res) {
        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);

        if (isPipeLikeCategory(updatedMaterial.materialCategory)) {
          // Update the material details with the new PO number
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              pipePurchaseOrderNumber: poNumber.trim(),
              // Also update forge fields if this is a forge material
            }
          }))
        }
        else if (updatedMaterial.materialCategory === 'Misc') {
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              miscPoNumber: poNumber.trim(),
            }
          }))
        }
        else {
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              forgePurchaseOrderNumber: poNumber.trim()
            }
          }))
        }
      }
     

    }

    setIsAddingPONumber(false);
  }

  const handleAddLocation = async (location: string) => {
    if (location.trim()) {
      if (locationOptions.find((tag: any) => tag.label === location.trim())) {
        Notification.warning(`${location.trim()} location already exists`);
        setNewTag('');
        return;
      }
      const apiPayload = {
        tags_name: location.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_LOCATION_TAGS',
        created_by: 'admin'
      }
      const res = await AddMaterialCategory(apiPayload, 'Location Tag');
      if (res) {
        // Update local state immediately for instant UI update
        const updatedLocalTags = [...localDocumentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);
        if (updatedMaterial.materialCategory === 'Forge') {
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              forgeLocation: location.trim(),
            }
          }))
        } else if (updatedMaterial.materialCategory === 'Misc') {
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              miscLocation: location.trim(),
            }
          }))
        } else {
          setUpdatedMaterial(prev => ({
            ...prev,
            details: {
              ...prev.details,
              pipeLocation: location.trim(),
            }
          }))
        }
      }
    }
    setIsAddingLocation(false);
  }

  const handleAddBasePipeOption = async (
    name: string,
    tagsType: string,
    existingOptions: { value: string; label: string }[],
    detailKey: 'pipeType' | 'pipePslLevel' | 'pipeDelivery',
    closeDialog: () => void,
    label: string,
  ) => {
    const trimmed = name.trim();
    if (!trimmed || !selectedPipe?.projectId) return;
    if (existingOptions.find((tag: any) => tag.label === trimmed)) {
      Notification.warning(`${trimmed} already exists`);
      closeDialog();
      return;
    }

    const res = await AddMaterialCategory(
      {
        tags_name: trimmed,
        project_id: selectedPipe.projectId,
        tags_type: tagsType,
        created_by: 'admin',
      },
      label
    );
    if (res) {
      const updatedLocalTags = [...localDocumentTags, res];
      setLocalDocumentTags(updatedLocalTags);
      setDocumentTags(updatedLocalTags);
      setUpdatedMaterial((prev) => ({
        ...prev,
        details: { ...prev.details, [detailKey]: trimmed },
      }));
    }
    closeDialog();
  };

  const handleAddPipeType = (name: string) =>
    handleAddBasePipeOption(name, 'MATERIAL_PIPE_TYPE_TAGS', pipeTypeOptions, 'pipeType', () => setIsAddingPipeType(false), 'Pipe type');

  const handleAddPipePslLevel = (name: string) =>
    handleAddBasePipeOption(name, 'MATERIAL_PIPE_PSL_LEVEL_TAGS', pipePslLevelOptions, 'pipePslLevel', () => setIsAddingPipePslLevel(false), 'Pipe PSL level');

  const handleAddPipeDelivery = (name: string) =>
    handleAddBasePipeOption(name, 'MATERIAL_PIPE_DELIVERY_TAGS', pipeDeliveryOptions, 'pipeDelivery', () => setIsAddingPipeDelivery(false), 'Pipe delivery');

  const handleAddMiscLotNumber = async (name: string) => {
    if (!name.trim() || !selectedPipe?.projectId) return;
    if (miscLotNumberTagOptions.find((tag: any) => tag.label === name.trim())) {
      Notification.warning(`${name.trim()} already exists`);
      setIsAddingMiscLot(false);
      return;
    }
    const res = await AddMaterialCategory(
      {
        tags_name: name.trim(),
        project_id: selectedPipe.projectId,
        tags_type: 'MATERIAL_MISC_LOT_NUMBER_TAGS',
        created_by: 'admin',
      },
      'Misc lot number'
    );
    if (res) {
      const updatedLocalTags = [...localDocumentTags, res];
      setLocalDocumentTags(updatedLocalTags);
      setDocumentTags(updatedLocalTags);
      setUpdatedMaterial((prev) => ({
        ...prev,
        details: { ...prev.details, miscLotNumber: name.trim() },
      }));
    }
    setIsAddingMiscLot(false);
  };

  const resetForm = () => {
    setUpdatedMaterial({
      materialCategory: 'Pipe',
      materialStatus: 'Available',
      details: {}
    });
    // Clear selected arrays
    setSelectedPipeGrade([]);
    setSelectedMaterialType([]);
    setSelectedManufacturer([]);
    setSelectedNDEManufacturer([]);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleCancel();
      }
    }}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>Update Material</DialogTitle>
        </DialogHeader>
        <div className="p-2">
        <div className="grid  p-2 grid-cols-4 gap-4 min-w-0  max-h-[75vh] flex flex-col min-h-[400px] overflow-y-auto    overflow-x-hidden">

            <div className="space-y-2">
              <Label>Material Category <span className="text-red-500">*</span></Label>
              <Select
                value={updatedMaterial.materialCategory}
                onValueChange={(value:
                  | 'Pipe'
                  | 'Bends'
                  | 'Flanges'
                  | 'Reducer'
                  | 'T-Piece'
                  | 'Weldolet'
                  | 'End Cap'
                  | 'Sweepolet'
                  | 'Hydro'
                  | 'Torquing'
                  | 'Gauging'
                  | 'Flushing'
                  | 'Forge'
                  | 'Gas'
                  | 'Wire Lot'
                  | 'NDE'
                  | 'Coating'
                  | 'Misc') =>
                  setUpdatedMaterial(prev => ({ ...prev, category: value }))
                }
                disabled={true}
              >
                <SelectTrigger className="bg-gray-100 cursor-not-allowed">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pipe">Pipe</SelectItem>
                  <SelectItem value="Bends">Bends</SelectItem>
                  <SelectItem value="Flanges">Flanges</SelectItem>
                  <SelectItem value="Reducer">Reducer</SelectItem>
                  <SelectItem value="T-Piece">T-Piece</SelectItem>
                  <SelectItem value="Weldolet">Weldolet</SelectItem>
                  <SelectItem value="End Cap">End Cap</SelectItem>
                  <SelectItem value="Sweepolet">Sweepolet</SelectItem>
                  <SelectItem value="Hydro">Hydro</SelectItem>
                  <SelectItem value="Torquing">Torquing</SelectItem>
                  <SelectItem value="Gauging">Gauging</SelectItem>
                  <SelectItem value="Flushing">Flushing</SelectItem>
                  <SelectItem value="Forge">Forge</SelectItem>
                  <SelectItem value="Gas">Gas</SelectItem>
                  <SelectItem value="Wire Lot">Wire Lot</SelectItem>
                  <SelectItem value="NDE">NDE</SelectItem>
                  <SelectItem value="Coating">Coating</SelectItem>
                  <SelectItem value="Misc">Misc</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status <span className="text-red-500">*</span></Label>
              <Select
                value={updatedMaterial.materialStatus == 'Consumed' ? updatedMaterial?.materialExtraDetails?.materialStatus || updatedMaterial.materialStatus || 'Available' :
                updatedMaterial.materialStatus == 'Shipped to NDT' ? 'Shipped to NDT' : 'Available'}
                disabled={updatedMaterial.materialStatus === 'Consumed' && !updatedMaterial.materialExtraDetails?.materialStatus}
                onValueChange={(value: 'Available' | 'Consumed' | 'Shipped to NDT') =>
                  setUpdatedMaterial((prev) => ({
                    ...prev,
                    materialStatus: value === 'Shipped to NDT' ? 'Consumed' : value,
                    materialExtraDetails: {
                      ...prev.materialExtraDetails,
                      materialStatus: value,
                    },
                   
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Consumed">Consumed</SelectItem>
                  <SelectItem value="Shipped to NDT">Shipped to NDT</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic fields based on category */}
            {isPipeLikeCategory(updatedMaterial.materialCategory) && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="pipeNRI">Pipe NRI</Label>
                  <Input
                    id="pipeNRI"
                    value={updatedMaterial.details?.pipeNRI || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeNRI: e.target.value }
                    }))}
                    placeholder="Enter Pipe NRI"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pipeNo">Pipe No <span className="text-red-500">*</span></Label>
                  <Input
                    id="pipeNo"
                    value={updatedMaterial.details?.pipeNo || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeNo: e.target.value }
                    }))}
                    placeholder="Enter Pipe No"
                  />
                </div>
                <div>
                  <Label htmlFor="pipeId">Pipe Id </Label>
                  <Input
                    id="pipeId"
                    value={updatedMaterial.details?.pipePipeId || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipePipeId: e.target.value }
                    }))}
                    placeholder="Enter Pipe Id"
                  />
                </div>
                <div>
                  <Label htmlFor="heatNumber">Heat Number</Label>
                  <Input
                    id="heatNumber"
                    value={updatedMaterial.details?.pipeHeatNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeHeatNumber: e.target.value }
                    }))}
                    placeholder="Enter Heat Number"
                  />
                </div>
                {updatedMaterial.materialCategory === 'Pipe' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="pipeLotNumber">Lot Number</Label>
                      <Input
                        id="pipeLotNumber"
                        value={updatedMaterial.details?.pipeLotNumber || ''}
                        onChange={(e) => setUpdatedMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeLotNumber: e.target.value }
                        }))}
                        placeholder="Enter Lot Number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pipeType">Pipe Type</Label>
                      <div className="relative mt-1">
                        <SearchableSelect
                          value={updatedMaterial.details?.pipeType}
                          onValueChange={(value: string) => {
                            setUpdatedMaterial(prev => ({
                              ...prev,
                              details: { ...prev.details, pipeType: value }
                            }))
                          }}
                          placeholder="Select pipe type"
                          searchPlaceholder="Search pipe type..."
                          options={pipeTypeOptions.map((tag: { label: string }) => ({ id: tag.label, name: tag.label }))}
                          onAddNew={() => setIsAddingPipeType(true)}
                          addNewText="Add New Pipe Type"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pipePslLevel">PSL Level</Label>
                      <div className="relative mt-1">
                        <SearchableSelect
                          value={updatedMaterial.details?.pipePslLevel}
                          onValueChange={(value: string) => {
                            setUpdatedMaterial(prev => ({
                              ...prev,
                              details: { ...prev.details, pipePslLevel: value }
                            }))
                          }}
                          placeholder="Select PSL level"
                          searchPlaceholder="Search PSL levels..."
                          options={pipePslLevelOptions.map((tag: { label: string }) => ({ id: tag.label, name: tag.label }))}
                          onAddNew={() => setIsAddingPipePslLevel(true)}
                          addNewText="Add New PSL Level"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pipeDelivery">Delivery</Label>
                      <div className="relative mt-1">
                        <SearchableSelect
                          value={updatedMaterial.details?.pipeDelivery}
                          onValueChange={(value: string) => {
                            setUpdatedMaterial(prev => ({
                              ...prev,
                              details: { ...prev.details, pipeDelivery: value }
                            }))
                          }}
                          placeholder="Select delivery"
                          searchPlaceholder="Search delivery..."
                          options={pipeDeliveryOptions.map((tag: { label: string }) => ({ id: tag.label, name: tag.label }))}
                          onAddNew={() => setIsAddingPipeDelivery(true)}
                          addNewText="Add New Delivery"
                        />
                      </div>
                    </div>
                  </>
                )}
               
                {/* <div className="space-y-2">
                  <Label htmlFor="length">Length (ft)</Label>
                  <NumberInput
                    id="length"
                    value={updatedMaterial.details?.pipeLength || ''}
                    onChange={(value) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeLength: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter Length"
                  />
                </div> */}
                {isDualDiameterMaterialCategory(updatedMaterial.materialCategory) ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="outerDiameter1">Diameter 1</Label>
                      <NumberInput
                        id="outerDiameter1"
                        showUnitSelect
                        unitValue={materialUnit.pipeOuterDiameter}
                        unitOptions={UNIT_OPTIONS}
                        onUnitChange={(v) => handleMaterialUnitChange('pipeOuterDiameter', v)}
                        value={updatedMaterial.details?.pipeOuterDiameter || ''}
                        onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, pipeOuterDiameter: value ? Number(value) : '' } }))}
                        placeholder="Enter Diameter 1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="outerDiameter2">Diameter 2</Label>
                      <NumberInput
                        id="outerDiameter2"
                        value={updatedMaterial.details?.pipeOuterDiameter2 || ''}
                        onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, pipeOuterDiameter2: value ? Number(value) : '' } }))}
                        placeholder="Enter Diameter 2"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="outerDiameter">Outer Diameter</Label>
                    <NumberInput
                      id="outerDiameter"
                      showUnitSelect
                      unitValue={materialUnit.pipeOuterDiameter}
                      unitOptions={UNIT_OPTIONS}
                      onUnitChange={(v) => handleMaterialUnitChange('pipeOuterDiameter', v)}
                      value={updatedMaterial.details?.pipeOuterDiameter || ''}
                      onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, pipeOuterDiameter: value ? Number(value) : '' } }))}
                      placeholder="Enter Outer Diameter"
                    />
                  </div>
                )}
                {isDualDiameterMaterialCategory(updatedMaterial.materialCategory) ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="wallThickness1">Wall Thickness 1</Label>
                      <NumberInput
                        id="wallThickness1"
                        showUnitSelect
                        unitValue={materialUnit.pipeWallThickness}
                        unitOptions={UNIT_OPTIONS}
                        onUnitChange={(v) => handleMaterialUnitChange('pipeWallThickness', v)}
                        value={updatedMaterial.details?.pipeWallThickness || ''}
                        onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, pipeWallThickness: value ? Number(value) : '' } }))}
                        placeholder="Enter Wall Thickness 1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wallThickness2">Wall Thickness 2</Label>
                      <NumberInput
                        id="wallThickness2"
                        value={updatedMaterial.details?.pipeWallThickness2 || ''}
                        onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, pipeWallThickness2: value ? Number(value) : '' } }))}
                        placeholder="Enter Wall Thickness 2"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="wallThickness">Wall Thickness</Label>
                    <NumberInput
                      id="wallThickness"
                      showUnitSelect
                      unitValue={materialUnit.pipeWallThickness}
                      unitOptions={UNIT_OPTIONS}
                      onUnitChange={(v) => handleMaterialUnitChange('pipeWallThickness', v)}
                      value={updatedMaterial.details?.pipeWallThickness || ''}
                      onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, pipeWallThickness: value ? Number(value) : '' } }))}
                      placeholder="Enter Wall Thickness"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Project Stage</Label>
                  <Select
                    value={updatedMaterial.details?.pipeProjectStage}
                    onValueChange={(value: 'PQT' | 'Production') =>
                      setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, pipeProjectStage: value } }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PQT">PQT</SelectItem>
                      <SelectItem value="Production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pipe Grade</Label>
                  <MultiSelect
                    key={`pipe-grade-${localDocumentTags.length}`}
                    options={pipeGradeOptions}
                    selected={selectedPipeGrade}
                    onChange={(value: string[]) => {
                      setSelectedPipeGrade(value);
                      setUpdatedMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, pipeGradeIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select pipe grade"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={true}
                    addButtonText="Add New Grade"
                    onAddButtonClick={() => setIsAddingPipeGrade(true)}
                    maxHeight="200px"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Material Type</Label>
                  <MultiSelect
                    key={`material-type-${localDocumentTags.length}`}
                    options={materialTypeOptions}
                    selected={selectedMaterialType}
                    onChange={(value: string[]) => {
                      setSelectedMaterialType(value);
                      setUpdatedMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, materialTypeIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select material type"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={true}
                    addButtonText="Add New Material Type"
                    onAddButtonClick={() => setIsAddingMaterialType(true)}
                    maxHeight="200px"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Manufacturer</Label>
                  <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedManufacturer(value);
                      setUpdatedMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={false}
                    maxHeight="200px"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="workOrderNumber">Work Order Number</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={updatedMaterial.details?.pipeWorkOrderNumber}
                      onValueChange={(value: string) => {
                        setUpdatedMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeWorkOrderNumber: value }
                        }))
                      }}
                      placeholder="Select work order number"
                      searchPlaceholder="Search work order numbers..."
                      options={workOrderNumberOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingWorkOrderNumber(true)}
                      addNewText="Add New Work Order Number"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="poNumber">Purchase Order Number</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={updatedMaterial.details?.pipePurchaseOrderNumber}
                      onValueChange={(value: string) => {
                        setUpdatedMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipePurchaseOrderNumber: value }
                        }))
                      }}
                      placeholder="Select Purchase Order number"
                      searchPlaceholder="Search Purchase Order numbers..."
                      options={poNumberOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingPONumber(true)}
                      addNewText="Add New Purchase Order Number"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={updatedMaterial.details?.pipeLocation}
                      onValueChange={(value: string) => {
                        setUpdatedMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, pipeLocation: value }
                        }))
                      }}
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      options={locationOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingLocation(true)}
                      addNewText="Add New Location"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lengthReceived">Length</Label>
                  <NumberInput
                    id="lengthReceived"
                    showUnitSelect
                    unitValue={materialUnit.pipeLength}
                    unitOptions={UNIT_OPTIONS}
                    onUnitChange={(v) => handleMaterialUnitChange('pipeLength', v)}
                    {...(pipeInventoryMode
                      ? {
                          min: 0,
                          step: 0.1,
                          wheelAdjust: true,
                          maxDecimalPlaces: 4,
                        }
                      : {})}
                    value={updatedMaterial.details?.pipeLength}
                    onChange={(value) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          pipeLength:
                            value === '' || value == null
                              ? ''
                              : pipeInventoryMode
                                ? parseNonNegativeNumber(String(value))
                                : Number(value),
                        },
                      }))
                    }
                    placeholder="Enter length"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lengthRemaining">Length remaining</Label>
                  <NumberInput
                    id="lengthRemaining"
                    showUnitSelect
                    unitValue={materialUnit.pipeLengthRemaining}
                    unitOptions={UNIT_OPTIONS}
                    onUnitChange={(v) => handleMaterialUnitChange('pipeLengthRemaining', v)}
                    value={updatedMaterial.details?.pipeLengthRemaining}
                    onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, pipeLengthRemaining: value ? Number(value) : '' } }))}
                    placeholder="Enter length remaining"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weightPerFt">Weight per ft (lbs)</Label>
                  <NumberInput
                    id="weightPerFt"
                    value={updatedMaterial.details?.pipeWeightPerFt}
                    onChange={(value) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeWeightPerFt: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter weight per foot in pounds"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wpsNumber">WPS Number</Label>
                  <Input
                    id="wpsNumber"
                    value={updatedMaterial.details?.pipeExtraDetails?.wpsNumber ?? ''}
                    onChange={(e) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          pipeExtraDetails: {
                            ...(prev.details?.pipeExtraDetails || {}),
                            wpsNumber: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="Enter WPS number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contractor">Contractor</Label>
                  <Input
                    id="contractor"
                    value={updatedMaterial.details?.pipeExtraDetails?.contractor ?? ''}
                    onChange={(e) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          pipeExtraDetails: {
                            ...(prev.details?.pipeExtraDetails || {}),
                            contractor: e.target.value,
                          },
                        },
                      }))
                    }
                    placeholder="Enter contractor"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="numberOfPups">Number of pups</Label>
                  <NumberInput
                    id="numberOfPups"
                    value={updatedMaterial.details?.pipeExtraDetails?.numberOfPups ?? ''}
                    min={0}
                    step={1}
                    wheelAdjust
                    onChange={(value) => {
                      if (pipeInventoryMode) {
                        handleInventoryPupsChange(value);
                        return;
                      }
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          pipeExtraDetails: {
                            ...(prev.details?.pipeExtraDetails || {}),
                            numberOfPups:
                              value === '' || value == null
                                ? ''
                                : parseNonNegativeInteger(value),
                          },
                        },
                      }));
                    }}
                    placeholder="Enter number of pups"
                  />
                </div>

                {pipeInventoryMode && (
                  <div className="space-y-2">
                    <Label className="invisible select-none text-sm font-medium" aria-hidden="true">
                      MTR received
                    </Label>
                    <div className="flex h-10 items-center gap-2">
                      <Checkbox
                        id="inventoryUpdateMtrReceived"
                        checked={Boolean(
                          (updatedMaterial.details as { mtrReceived?: boolean } | undefined)
                            ?.mtrReceived
                        )}
                        onCheckedChange={(c) =>
                          setUpdatedMaterial((prev) => ({
                            ...prev,
                            details: { ...prev.details, mtrReceived: c === true },
                          }))
                        }
                      />
                      <Label htmlFor="inventoryUpdateMtrReceived" className="font-normal cursor-pointer">
                        MTR received
                      </Label>
                    </div>
                  </div>
                )}

                {pipeInventoryMode && inventorySegments.length > 0 && (
                  <div className="space-y-3 col-span-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-medium">Pipe Segments</h3>
                      <Button
                        type="button"
                        onClick={() => {
                          const next = [
                            ...inventorySegments,
                            createEmptyWarehouseSegment(
                              inventorySegments.length,
                              (updatedMaterial.details as { dateRecorded?: string } | undefined)
                                ?.dateRecorded?.slice(0, 10) || dayjs().format('YYYY-MM-DD')
                            ),
                          ];
                          setInventorySegments(next, next.length);
                        }}
                        className="flex items-center gap-2"
                        size="sm"
                      >
                        <Plus className="h-4 w-4" />
                        Add Segment
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Segment Name
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Segment Length
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Date Inventory
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Used
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {inventorySegments.map((segment) => (
                            <tr key={segment.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <Input
                                  value={segment.segmentName}
                                  onChange={(e) =>
                                    setInventorySegments(
                                      inventorySegments.map((s) =>
                                        s.id === segment.id
                                          ? { ...s, segmentName: e.target.value }
                                          : s
                                      )
                                    )
                                  }
                                  className="w-full min-w-[120px]"
                                  placeholder="A"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <NumberInput
                                  value={segment.segmentLength}
                                  min={0}
                                  step={0.1}
                                  wheelAdjust
                                  emptyWhenZero={false}
                                  maxDecimalPlaces={4}
                                  onChange={(value) =>
                                    setInventorySegments(
                                      inventorySegments.map((s) =>
                                        s.id === segment.id
                                          ? {
                                              ...s,
                                              segmentLength:
                                                value === ''
                                                  ? 0
                                                  : parseNonNegativeNumber(value),
                                            }
                                          : s
                                      )
                                    )
                                  }
                                  className="w-full min-w-[140px]"
                                  placeholder="0.0"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <AntdDate
                                  type="date"
                                  value={segment.dateInventory || ''}
                                  onChange={(dateVal) =>
                                    setInventorySegments(
                                      inventorySegments.map((s) =>
                                        s.id === segment.id
                                          ? {
                                              ...s,
                                              dateInventory: dateVal
                                                ? String(dateVal).slice(0, 10)
                                                : '',
                                            }
                                          : s
                                      )
                                    )
                                  }
                                  className="w-full min-w-[160px]"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Checkbox
                                  checked={segment.used}
                                  onCheckedChange={(checked) =>
                                    setInventorySegments(
                                      inventorySegments.map((s) =>
                                        s.id === segment.id
                                          ? { ...s, used: checked === true }
                                          : s
                                      )
                                    )
                                  }
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Button
                                  type="button"
                                  onClick={() => {
                                    const next = inventorySegments.filter((s) => s.id !== segment.id);
                                    setInventorySegments(next, next.length ? next.length : '');
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Marking</Label>
                  <textarea
                    value={updatedMaterial.details?.pipeMarking || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeMarking: e.target.value }
                    }))}
                    className="w-full mt-2 p-3 border border-gray-300 min-h-[60px] rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                    placeholder="Enter marking..."
                  />
                </div>
                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Notes</Label>
                  <textarea
                    value={updatedMaterial.details?.pipeNotes || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, pipeNotes: e.target.value }
                    }))}
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                    placeholder="Enter notes..."
                  />
                </div>
              </>
            )}

            {updatedMaterial.materialCategory === 'Forge' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="forgeNRI">Forge NRI <span className="text-red-500">*</span></Label>
                  <Input
                    id="forgeNRI"
                    value={updatedMaterial.details?.forgeNRI || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeNRI: e.target.value }
                    }))}
                    placeholder="Enter Forge NRI"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forgeSerialNumber">Forge Serial Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="forgeSerialNumber"
                    value={updatedMaterial.details?.forgeSerialNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeSerialNumber: e.target.value }
                    }))}
                    placeholder="Enter Forge Serial Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forgeHeatNumber">Forge Heat Number </Label>
                  <Input
                    id="forgeHeatNumber"
                    value={updatedMaterial.details?.forgeHeatNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeHeatNumber: e.target.value }
                    }))}
                    placeholder="Enter Forge Heat Number"
                  />
                </div>
               
               
                <div className="space-y-2">
                  <Label htmlFor="workOrderNumber">Work Order Number</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={updatedMaterial.details?.forgeWorkOrderNumber}
                      onValueChange={(value: string) => {
                        setUpdatedMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, forgeWorkOrderNumber: value }
                        }))
                      }}
                      placeholder="Select work order number"
                      searchPlaceholder="Search work order numbers..."
                      options={workOrderNumberOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingWorkOrderNumber(true)}
                      addNewText="Add New Work Order Number"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="poNumber">Purchase Order Number</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={updatedMaterial.details?.forgePurchaseOrderNumber}
                      onValueChange={(value: string) => {
                        setUpdatedMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, forgePurchaseOrderNumber: value }
                        }))
                      }}
                      placeholder="Select Purchase Order number"
                      searchPlaceholder="Search Purchase Order numbers..."
                      options={poNumberOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingPONumber(true)}
                      addNewText="Add New Purchase Order Number"
                    />
                  </div>
                </div>


              
            
                <div className="space-y-2">
                  <Label htmlFor="forge-outerDiameter">Outer Diameter</Label>
                  <NumberInput
                    id="forge-outerDiameter"
                    showUnitSelect
                    unitValue={materialUnit.forgeOuterDiameter}
                    unitOptions={UNIT_OPTIONS}
                    onUnitChange={(v) => handleMaterialUnitChange('forgeOuterDiameter', v)}
                    value={updatedMaterial.details?.forgeOuterDiameter}
                    onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, forgeOuterDiameter: value ? Number(value) : '' } }))}
                    placeholder="Enter Outer Diameter"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forge-wallThickness">Wall Thickness</Label>
                  <NumberInput
                    id="forge-wallThickness"
                    showUnitSelect
                    unitValue={materialUnit.forgeWallThickness}
                    unitOptions={UNIT_OPTIONS}
                    onUnitChange={(v) => handleMaterialUnitChange('forgeWallThickness', v)}
                    value={updatedMaterial.details?.forgeWallThickness}
                    onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, forgeWallThickness: value ? Number(value) : '' } }))}
                    placeholder="Enter Wall Thickness"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pipe Grade</Label>
                  <MultiSelect
                    options={pipeGradeOptions}
                    selected={selectedPipeGrade}
                    onChange={(value: string[]) => {
                      setSelectedPipeGrade(value);
                      setUpdatedMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, forgeGradeIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select pipe grade"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={true}
                    addButtonText="Add New Grade"
                    onAddButtonClick={() => setIsAddingPipeGrade(true)}
                    maxHeight="200px"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Manufacturer</Label>
                  <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedManufacturer(value);
                      setUpdatedMaterial(prev => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map(id => Number(id)) }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={false}
                    // addButtonText="Add New Manufacturer"
                    // onAddButtonClick={() => setIsAddingManufacturer(true)}
                    maxHeight="200px"
                  />
                </div>
               
               
               
                <div className="space-y-2">
                  <Label>Project Stage</Label>
                  <Select
                    value={updatedMaterial.details?.forgeProjectStage}
                    onValueChange={(value: 'PQT' | 'Production') =>
                      setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, forgeProjectStage: value } }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PQT">PQT</SelectItem>
                      <SelectItem value="Production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={updatedMaterial.details?.forgeLocation}
                      onValueChange={(value: string) => {
                        setUpdatedMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, forgeLocation: value }
                        }))
                      }}
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      options={locationOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingLocation(true)}
                      addNewText="Add New Location"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forge-length">Length</Label>
                  <NumberInput
                    id="forge-length"
                    showUnitSelect
                    unitValue={materialUnit.forgeLength}
                    unitOptions={UNIT_OPTIONS}
                    onUnitChange={(v) => handleMaterialUnitChange('forgeLength', v)}
                    value={updatedMaterial.details?.forgeLength}
                    onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, forgeLength: value ? Number(value) : '' } }))}
                    placeholder="Enter Length"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forge-lengthRemaining">Length remaining</Label>
                  <NumberInput
                    id="forge-lengthRemaining"
                    showUnitSelect
                    unitValue={materialUnit.forgeLengthRemaining}
                    unitOptions={UNIT_OPTIONS}
                    onUnitChange={(v) => handleMaterialUnitChange('forgeLengthRemaining', v)}
                    value={updatedMaterial.details?.forgeLengthRemaining}
                    onChange={(value) => setUpdatedMaterial(prev => ({ ...prev, details: { ...prev.details, forgeLengthRemaining: value ? Number(value) : '' } }))}
                    placeholder="Enter length remaining"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weightPerFt">Weight per ft (lbs)</Label>
                  <NumberInput
                    id="weightPerFt"
                    value={updatedMaterial.details?.forgeWeightPerFt}
                    onChange={(value) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeWeightPerFt: value ? Number(value) : '' }
                    }))}
                    placeholder="Enter weight per foot in pounds"
                  />
                </div>
                
                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Notes</Label>
                  <textarea
                    value={updatedMaterial.details?.forgeNotes || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, forgeNotes: e.target.value }
                    }))}
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                    placeholder="Enter forge notes..."
                  />
                </div>
              </>
            )}

            {updatedMaterial.materialCategory === 'Wire Lot' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="wireLotNumber">Lot/Certification Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="wireLotNumber"
                    value={updatedMaterial.details?.wireLotNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireLotNumber: e.target.value }
                    }))}
                    placeholder="Enter Lot Number"
                  />
                </div>
               

                <div className="space-y-2">
                  <Label htmlFor="wireType">Consumable Type</Label>
                  <Input
                    id="wireType"
                    value={updatedMaterial.details?.wireType || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireType: e.target.value }
                    }))}
                    placeholder="Enter Wire Type"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireSizeNumber">Size </Label>
                  <Input
                    id="wireSizeNumber"
                    value={updatedMaterial.details?.wireExtraDetails?.wireSizeNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireExtraDetails: { ...prev.details.wireExtraDetails, wireSizeNumber: e.target.value } }
                    }))}
                    placeholder="Enter Wire Size"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireSerialNumber">Serial Number </Label>
                  <Input
                    id="wireSerialNumber"
                    value={updatedMaterial.details?.wireSerialNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireSerialNumber: e.target.value }
                    }))}
                    placeholder="Enter Wire Serial Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireManufacturer">Manufacturer </Label>
                  <Input
                    id="wireManufacturer"
                    value={updatedMaterial.details?.wireExtraDetails?.wireManufacturer || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireExtraDetails: { ...prev.details.wireExtraDetails, wireManufacturer: e.target.value } }
                    }))}
                    placeholder="Enter Manufacturer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireBatchNumber">Batch Number</Label>
                  <Input
                    id="wireBatchNumber"
                    value={updatedMaterial.details?.wireBatchNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireBatchNumber: e.target.value }
                    }))}
                    placeholder="Enter Batch Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wireClassification">Wire Classification</Label>
                  <Input
                    id="wireClassification"
                    value={updatedMaterial.details?.wireExtraDetails?.wireClassification || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, wireExtraDetails: { ...prev.details.wireExtraDetails, wireClassification: e.target.value } }
                    }))}
                    placeholder="Enter Wire Classification"
                  />
                </div>
              </>
            )}

            {updatedMaterial.materialCategory === 'Gas' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="gasSerialNumber">Clyinder/Serial Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="gasSerialNumber"
                    value={updatedMaterial.details?.gasSerialNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasSerialNumber: e.target.value }
                    }))}
                    placeholder="Enter Gas Serial Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasLotNumber">Lot Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="gasLotNumber"
                    value={updatedMaterial.details?.gasLotNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasLotNumber: e.target.value }
                    }))}
                    placeholder="Enter Gas Lot Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasType">Consumable Type</Label>
                  <Input
                    id="gasType"
                    value={updatedMaterial.details?.gasType || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasType: e.target.value }
                    }))}
                    placeholder="Enter Consumable Type"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasSize">Gas Size</Label>
                  <Input
                    id="gasSize"
                    value={updatedMaterial.details?.gasExtraDetails?.gasSize || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasExtraDetails: { ...prev.details.gasExtraDetails, gasSize: e.target.value } }
                    }))}
                    placeholder="Enter Gas Size"
                  />
                </div>
               

                <div className="space-y-2">
                  <Label htmlFor="gasManufacturer">Manufacturer </Label>
                  <Input
                    id="gasManufacturer"
                    value={updatedMaterial.details?.gasExtraDetails?.gasManufacturer || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasExtraDetails: { ...prev.details.gasExtraDetails, gasManufacturer: e.target.value } }
                    }))}
                    placeholder="Enter Manufacturer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasBatchNumber">Batch Number</Label>
                  <Input
                    id="gasBatchNumber"
                    value={updatedMaterial.details?.gasBatchNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasBatchNumber: e.target.value }
                    }))}
                    placeholder="Enter Batch Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gasComposition">Gas Composition</Label>
                  <Input
                    id="gasComposition"
                    value={updatedMaterial.details?.gasExtraDetails?.gasComposition || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, gasExtraDetails: { ...prev.details.gasExtraDetails, gasComposition: e.target.value } }
                    }))}
                    placeholder="Enter Gas Composition"
                  />
                </div>
              </>
            )}

            {updatedMaterial.materialCategory === 'Coating' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="coatingLotNumber">Lot Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="coatingLotNumber"
                    value={updatedMaterial.details?.coatingLotNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, coatingLotNumber: e.target.value }
                    }))}
                    placeholder="Enter Lot Number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coatingManufacturer">Manufacturer</Label>
                  <Input
                    id="coatingManufacturer"
                    value={updatedMaterial.details?.coatingExtraDetails?.coatingManufacturer || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, coatingExtraDetails: { ...prev.details.coatingExtraDetails, coatingManufacturer: e.target.value } }
                    }))}
                    placeholder="Enter Manufacturer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coatingBatchNumber">Batch Number</Label>
                  <Input
                    id="coatingBatchNumber"
                    value={updatedMaterial.details?.coatingBatchNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, coatingBatchNumber: e.target.value }
                    }))}
                    placeholder="Enter Batch Number"
                  />
                </div>
              </>
            )}

            {updatedMaterial.materialCategory === 'Misc' && (
              <>
                <div className="space-y-2">
                  <Label>
                    Material type <span className="text-red-500">*</span>
                  </Label>
                  <SearchableSelect
                    value={updatedMaterial.details?.miscMaterialTypeName}
                    onValueChange={(value: string) => {
                      const opt =
                        miscMaterialTypeSelectOptions.find((o) => o.label === value) ??
                        miscMaterialTypeOptions.find((o) => o.label === value) ??
                        materialTypeOptions.find((o) => o.label === value);
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          miscMaterialTypeName: value,
                          miscMaterialTypeIds: opt ? [Number(opt.value)] : [],
                        },
                      }));
                    }}
                    placeholder="Select misc material type"
                    searchPlaceholder="Search misc material types..."
                    options={miscMaterialTypeSelectOptions.map((tag) => ({
                      id: tag.value,
                      name: tag.label,
                    }))}
                    onAddNew={() => setIsAddingMiscMaterialType(true)}
                    addNewText="Add New Misc Material Type"
                    showAddButton={true}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscPartNumber">Part number</Label>
                  <Input
                    id="miscPartNumber"
                    value={updatedMaterial.details?.miscPartNumber || ''}
                    onChange={(e) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscPartNumber: e.target.value },
                      }))
                    }
                    placeholder="Enter part number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscHeatNumber">Heat number</Label>
                  <Input
                    id="miscHeatNumber"
                    value={updatedMaterial.details?.miscHeatNumber || ''}
                    onChange={(e) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscHeatNumber: e.target.value },
                      }))
                    }
                    placeholder="Enter heat number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Lot number</Label>
                  <SearchableSelect
                    value={updatedMaterial.details?.miscLotNumber}
                    onValueChange={(value: string) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscLotNumber: value },
                      }))
                    }
                    placeholder="Select lot number"
                    searchPlaceholder="Search lot numbers..."
                    options={miscLotNumberTagOptions.map((tag) => ({ id: tag.value, name: tag.label }))}
                    onAddNew={() => setIsAddingMiscLot(true)}
                    addNewText="Add New Lot Number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Job number</Label>
                  <SearchableSelect
                    value={updatedMaterial.details?.miscJobNumber}
                    onValueChange={(value: string) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscJobNumber: value },
                      }))
                    }
                    placeholder="Select job number"
                    searchPlaceholder="Search job numbers..."
                    options={workOrderNumberOptions.map((tag) => ({ id: tag.value, name: tag.label }))}
                    onAddNew={() => setIsAddingWorkOrderNumber(true)}
                    addNewText="Add New Job Number"
                  />
                </div>

                <div className="space-y-2">
                  <Label>PO number</Label>
                  <SearchableSelect
                    value={updatedMaterial.details?.miscPoNumber}
                    onValueChange={(value: string) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscPoNumber: value },
                      }))
                    }
                    placeholder="Select PO number"
                    searchPlaceholder="Search PO numbers..."
                    options={poNumberOptions.map((tag) => ({ id: tag.value, name: tag.label }))}
                    onAddNew={() => setIsAddingPONumber(true)}
                    addNewText="Add New PO Number"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscQuantity">
                    Quantity <span className="text-red-500">*</span>
                  </Label>
                  <NumberInput
                    id="miscQuantity"
                    value={updatedMaterial.details?.miscQuantity}
                    onChange={(value) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          miscQuantity: value !== '' && value != null ? Number(value) : '',
                        },
                      }))
                    }
                    placeholder="Enter quantity"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscSizeOrWeight">Size / weight</Label>
                  <Input
                    id="miscSizeOrWeight"
                    value={updatedMaterial.details?.miscSizeOrWeight || ''}
                    onChange={(e) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscSizeOrWeight: e.target.value },
                      }))
                    }
                    placeholder="e.g. 10 ft, 25 kg"
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label>Manufacturer</Label>
                  <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedManufacturer(value);
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, manufacturerIds: value.map((id) => Number(id)) },
                      }));
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    maxHeight="200px"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Production type</Label>
                  <Select
                    value={
                      updatedMaterial.details?.miscProductionType ??
                      updatedMaterial.details?.miscProjectStage
                    }
                    onValueChange={(value: 'Welding' | 'Coating') =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: {
                          ...prev.details,
                          miscProductionType: value,
                          miscProjectStage: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select production type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Welding">Welding</SelectItem>
                      <SelectItem value="Coating">Coating</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="miscLocation">Location</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={updatedMaterial.details?.miscLocation}
                      onValueChange={(value: string) =>
                        setUpdatedMaterial((prev) => ({
                          ...prev,
                          details: { ...prev.details, miscLocation: value },
                        }))
                      }
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      options={locationOptions.map((tag) => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingLocation(true)}
                      addNewText="Add New Location"
                    />
                  </div>
                </div>

                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Note</Label>
                  <textarea
                    value={updatedMaterial.details?.miscNotes || ''}
                    onChange={(e) =>
                      setUpdatedMaterial((prev) => ({
                        ...prev,
                        details: { ...prev.details, miscNotes: e.target.value },
                      }))
                    }
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:border-blue-400"
                    placeholder="Notes..."
                  />
                </div>
              </>
            )}

            {updatedMaterial.materialCategory === 'NDE' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="ndeConsumableType">Consumable Type <span className="text-red-500">*</span></Label>
                  <Input
                    id="ndeConsumableType"
                    value={updatedMaterial.details?.consumableType || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, consumableType: e.target.value }
                    }))}
                    placeholder="Enter consumable type"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ndeType">Type</Label>
                  <Input
                    id="ndeType"
                    value={updatedMaterial.details?.type || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, type: e.target.value }
                    }))}
                    placeholder="Enter type (e.g., ER70S-6)"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ndeSerialNumber">Serial Number</Label>
                  <Input
                    id="ndeSerialNumber"
                    value={updatedMaterial.details?.serialNumber || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, serialNumber: e.target.value }
                    }))}
                    placeholder="Enter serial number"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Manufacturer</Label>
                  <MultiSelect
                    options={manufacturerOptions}
                    selected={selectedNDEManufacturer}
                    onChange={(value: string[]) => {
                      setSelectedNDEManufacturer(value);
                      // Get labels from manufacturerOptions based on selected IDs
                      const selectedLabels = value.map((id: string) => {
                        const option = manufacturerOptions.find((opt: any) => opt.value === id);
                        return option ? option.label : '';
                      }).filter((label: string) => label !== '');
                      
                      setUpdatedMaterial(prev => ({
                        ...prev,
                        details: { 
                          ...prev.details, 
                          manufacturerIds: value.map(id => Number(id)),
                          ndeExtraDetails: { 
                            ...prev.details.ndeExtraDetails, 
                            ndeManufacturer: selectedLabels.join(', ') 
                          }
                        }
                      }))
                    }}
                    placeholder="Select manufacturer"
                    isSearchable={true}
                    className="w-full"
                    showAddButton={false}
                    maxHeight="200px"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Expiry Date</Label>
                  <div className="flex items-center space-x-2">
                    <AntdDate
                      name="ndeExpDate"
                      value={updatedMaterial.details?.ndeExtraDetails?.ndeExpDate || updatedMaterial.details?.expDate || updatedMaterial.details?.expiry_date || ''}
                      onChange={(value: string) => setUpdatedMaterial(prev => ({
                        ...prev,
                        details: { 
                          ...prev.details, 
                          ndeExtraDetails: { 
                            ...prev.details.ndeExtraDetails, 
                            ndeExpDate: value 
                          }
                        }
                      }))}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ndeMaterialId">Material ID</Label>
                  <Input
                    id="ndeMaterialId"
                    value={updatedMaterial.details?.ndeExtraDetails?.ndeMaterialId || updatedMaterial.details?.materialId || updatedMaterial.details?.material_id || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { 
                        ...prev.details, 
                        ndeExtraDetails: { 
                          ...prev.details.ndeExtraDetails, 
                          ndeMaterialId: e.target.value 
                        }
                      }
                    }))}
                    placeholder="Enter material ID"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ndeLocation">Location</Label>
                  <div className="relative mt-1">
                    <SearchableSelect
                      value={updatedMaterial.details?.location || ''}
                      onValueChange={(value: string) => {
                        setUpdatedMaterial(prev => ({
                          ...prev,
                          details: { ...prev.details, location: value }
                        }))
                      }}
                      placeholder="Select location"
                      searchPlaceholder="Search locations..."
                      options={locationOptions.map(tag => ({ id: tag.value, name: tag.label }))}
                      onAddNew={() => setIsAddingLocation(true)}
                      addNewText="Add New Location"
                    />
                  </div>
                </div>
                <div className="space-y-2 col-span-4">
                  <Label className="text-base font-medium">Notes</Label>
                  <textarea
                    value={updatedMaterial.details?.notes || ''}
                    onChange={(e) => setUpdatedMaterial(prev => ({
                      ...prev,
                      details: { ...prev.details, notes: e.target.value }
                    }))}
                    className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                    placeholder="Enter notes..."
                  />
                </div>
              </>
            )}

           
          </div>
          <div className="flex justify-end gap-2 pt-4 col-span-4">
              <Button
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateMaterial}
                className="bg-[#EB1C37] text-white px-4 py-2 rounded-lg hover:bg-[#EB1C37]/90 transition-colors"
                disabled={!updatedMaterial.materialCategory || isSubmitting}
                loading={isSubmitting}
                loadingText="Updating Material..."
              >
                {'Update Material'}
              </Button>
            </div>
        </div>
      </DialogContent>

      {/* Add New Pipe Grade Dialog */}
      <AddNewItemDialog
        open={isAddingPipeGrade}
        onOpenChange={setIsAddingPipeGrade}
        title="Add New Grade"
        label="Pipe Grade"
        placeholder="Enter pipe grade"
        onAdd={handleAddPipeGrade}
        buttonText="Add Grade"
        cancelText="Cancel"
      />

      {/* Add New Material Type Dialog */}
      <AddNewItemDialog
        open={isAddingMaterialType}
        onOpenChange={setIsAddingMaterialType}
        title="Add New Material Type"
        label="Material Type"
        placeholder="Enter material type"
        onAdd={handleAddMaterialType}
        buttonText="Add Material Type"
        cancelText="Cancel"
      />

      {/* Add New Work Order Number Dialog */}
      <AddNewItemDialog
        open={isAddingWorkOrderNumber}
        onOpenChange={setIsAddingWorkOrderNumber}
        title="Add New Work Order Number"
        label="Work Order Number"
        placeholder="Enter work order number"
        onAdd={handleAddWorkOrderNumber}
        buttonText="Add Work Order Number"
        cancelText="Cancel"
      />

      {/* Add New PO Number Dialog */}
      <AddNewItemDialog
        open={isAddingPONumber}
        onOpenChange={setIsAddingPONumber}
        title="Add New PO Number"
        label="PO Number"
        placeholder="Enter PO number"
        onAdd={handleAddPONumber}
        buttonText="Add PO Number"
        cancelText="Cancel"
      />

      {/* Add New Location Dialog */}
      <AddNewItemDialog
        open={isAddingLocation}
        onOpenChange={setIsAddingLocation}
        title="Add New Location"
        label="Location"
        placeholder="Enter location"
        onAdd={handleAddLocation}
        buttonText="Add Location"
        cancelText="Cancel"
      />

      <AddNewItemDialog
        open={isAddingPipeType}
        onOpenChange={setIsAddingPipeType}
        title="Add New Pipe Type"
        label="Pipe Type"
        placeholder="Enter pipe type"
        onAdd={handleAddPipeType}
        buttonText="Add Pipe Type"
        cancelText="Cancel"
      />

      <AddNewItemDialog
        open={isAddingPipePslLevel}
        onOpenChange={setIsAddingPipePslLevel}
        title="Add New PSL Level"
        label="PSL Level"
        placeholder="Enter PSL level"
        onAdd={handleAddPipePslLevel}
        buttonText="Add PSL Level"
        cancelText="Cancel"
      />

      <AddNewItemDialog
        open={isAddingPipeDelivery}
        onOpenChange={setIsAddingPipeDelivery}
        title="Add New Delivery"
        label="Delivery"
        placeholder="Enter delivery"
        onAdd={handleAddPipeDelivery}
        buttonText="Add Delivery"
        cancelText="Cancel"
      />

      {isAddingMiscLot && (
        <AddNewItemDialog
          open={isAddingMiscLot}
          onOpenChange={setIsAddingMiscLot}
          title="Add Lot Number"
          label="Lot number"
          placeholder="Enter lot number"
          onAdd={handleAddMiscLotNumber}
          buttonText="Add"
          cancelText="Cancel"
        />
      )}

      {isAddingMiscMaterialType && (
        <AddNewItemDialog
          open={isAddingMiscMaterialType}
          onOpenChange={setIsAddingMiscMaterialType}
          title="Add Misc Material Type"
          label="Misc material type"
          placeholder="Enter misc material type"
          onAdd={handleAddMiscMaterialType}
          buttonText="Add"
          cancelText="Cancel"
        />
      )}
    </Dialog>
  );
}; 